import React, { useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { View, Text, TextInput } from '../../lib/react-native-compat';
import { selectedJobDataAtom, userNameAtom, jobCostDetailAtom, jobPhaseCodesAtom } from '../../store/company';
import { loadJobRoster } from '../../db/company';
import { fieldReportAtom, fieldReportDirtyAtom } from './state';
import { isBlankRow, jobWorkTypes } from './sectionDefs';
import SectionRowsStep from './SectionRowsStep';
import AutoGrowTextarea from './AutoGrowTextarea';
import WeatherBlock from './WeatherBlock';

// 'YYYY-MM-DD' → 'MM/DD/YYYY' without a Date round-trip (no TZ shift).
const fmtDate = (iso) => {
    const [y, m, d] = String(iso || '').split('-');
    return y && m && d ? `${m}/${d}/${y}` : '';
};

const inputBase = {
    minHeight: 44,
    fontSize: 16,
    borderRadius: 8,
    border: '1px solid #CBD5E1',
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 12,
    paddingRight: 12,
    width: '100%',
    backgroundColor: '#fff',
    color: '#0F172A',
    boxSizing: 'border-box',
};

const labelStyle = {
    fontSize: 11,
    fontWeight: '600',
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
};

// Shares a row evenly however many siblings there are, so a two- or
// three-field row stays on one line instead of wrapping at the 48% width.
const FlexField = ({ label, children }) => (
    <View style={{ flex: 1, minWidth: 180, marginBottom: 12 }}>
        <Text style={labelStyle}>{label}</Text>
        {children}
    </View>
);

const WideField = ({ label, children }) => (
    <View style={{ width: '100%', marginBottom: 12 }}>
        <Text style={labelStyle}>{label}</Text>
        {children}
    </View>
);

// embedded: the Quick Report has no Setup tab — this block renders inside the
// review page instead, so the big blue project card is dropped (the review's
// own identity card covers it, and carries the weather block).
const SetupStep = ({ readOnly, simple, embedded }) => {
    const [report, setReport] = useAtom(fieldReportAtom);
    const [, setDirty] = useAtom(fieldReportDirtyAtom);
    const [jobData] = useAtom(selectedJobDataAtom);
    const [userName] = useAtom(userNameAtom);
    const [jobCostDetail] = useAtom(jobCostDetailAtom);
    const [phaseCodes] = useAtom(jobPhaseCodesAtom);

    // The job's project type (Form_Job_Master Work_Type) for the header card.
    const workTypes = useMemo(
        () => jobWorkTypes(jobData, jobCostDetail, phaseCodes),
        [jobData, jobCostDetail, phaseCodes],
    );

    // Superintendent dropdown options from the job roster (still free-form —
    // the datalist only suggests). The selected job's own super prefills an
    // empty field.
    const [supers, setSupers] = useState([]);
    const buId = report?.buId;
    useEffect(() => {
        if (!buId) return undefined;
        let cancelled = false;
        loadJobRoster()
            .then((res) => {
                if (cancelled) return;
                const rows = res && res.data ? res.data : [];
                setSupers(Array.from(new Set(
                    rows.map((r) => r.Project_Superintendent).filter(Boolean),
                )).sort());
                const mine = rows.find((r) => String(r.Job_Number) === String(buId));
                if (mine?.Project_Superintendent) {
                    setReport((r) => (r && !r.superintendent && r.status !== 'Submitted')
                        ? { ...r, superintendent: mine.Project_Superintendent } : r);
                }
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [buId, setReport]);

    // Prepared By defaults to the signed-in user once the login lands.
    useEffect(() => {
        if (!userName) return;
        setReport((r) => (r && !r.preparedBy && r.status !== 'Submitted')
            ? { ...r, preparedBy: userName } : r);
    }, [userName, setReport]);

    if (!report) return null;

    const set = (name) => (value) => {
        setReport({ ...report, [name]: value });
        setDirty(true);
    };

    // Safety lives on this tab but is stored as the first row of the safety
    // section, so save/review/sheet keep working unchanged. Incidents and
    // Near Misses also mirror to the report header columns.
    const safetyRows = report.sections?.safety || [];
    const safety = safetyRows[0] || {};
    const patchSafety = (patch) => {
        const next = { ...safety, ...patch };
        setReport({
            ...report,
            ...(Object.prototype.hasOwnProperty.call(patch, 'incidents') ? { safetyIncidents: patch.incidents } : null),
            sections: { ...report.sections, safety: [next, ...safetyRows.slice(1)] },
        });
        setDirty(true);
    };
    const setSafety = (name) => (value) => patchSafety({ [name]: value });

    // Quick Report answer: "Any safety incidents today?" Yes reveals the count
    // + detail; No records zero events and clears anything typed under Yes.
    // Reports started in the full report answer the question by their event
    // count, so a count > 0 reads as Yes until the box is touched.
    const anyIncidents = safety.anyIncidents || (parseFloat(safety.incidents) > 0 ? 'Y' : '');
    const setAnyIncidents = (value) => patchSafety(value === 'N'
        ? { anyIncidents: 'N', incidents: '0', incidentDetail: '' }
        : { anyIncidents: value });

    const jobName = jobData?.BU_Description || jobData?.Job_Description || '';
    const roStyle = readOnly ? { backgroundColor: '#F1F5F9' } : null;

    // The Issue Log lives on this tab behind a checkbox instead of its own
    // wizard page. Rows still store as the 'delays' section, so save/review/
    // sheet are unchanged. issuesOpen is a transient flag on the report (never
    // saved — buildSavePayload picks its fields explicitly); reopened reports
    // fall back to "checked if any real rows exist". Unchecking resets the
    // section to one blank starter row so hidden entries never persist.
    const issueRows = report.sections?.delays || [];
    const hasIssues = issueRows.some((r) => !isBlankRow(r));
    const issuesOpen = readOnly ? hasIssues : (report.issuesOpen ?? hasIssues);
    const toggleIssues = (checked) => {
        setReport({
            ...report,
            issuesOpen: checked,
            sections: checked ? report.sections : { ...report.sections, delays: [{}] },
        });
        if (!checked && hasIssues) setDirty(true);
    };

    // Yes / No checkbox pair on a safety field. Stored as 'Y' / 'N' (same bool
    // convention the section defs / sheet view use); checking one clears the
    // other, re-clicking a checked box clears the answer.
    const ynBox = (name, val, caption) => (
        <label style={{
            display: 'flex', alignItems: 'center', gap: 6, width: 64,
            cursor: readOnly ? 'default' : 'pointer',
        }}>
            <input
                type="checkbox"
                checked={safety[name] === val}
                disabled={readOnly}
                onChange={(e) => setSafety(name)(e.target.checked ? val : '')}
                style={{ width: 18, height: 18, accentColor: '#1D4ED8', cursor: 'inherit' }}
            />
            <Text style={{ fontSize: 14, color: '#0F172A' }}>{caption}</Text>
        </label>
    );

    // One aligned row in the Yes/No list: label on the left, the Yes and No
    // boxes lined up in fixed columns so every row matches.
    const ynRow = (name, label, last) => (
        <View style={{
            flexDirection: 'row', alignItems: 'center', minHeight: 44,
            paddingLeft: 12, paddingRight: 12, columnGap: 16,
            borderBottom: last ? 'none' : '1px solid #E2E8F0',
        }}>
            <Text style={{ flex: 1, fontSize: 15, color: '#0F172A' }}>{label}</Text>
            {ynBox(name, 'Y', 'Yes')}
            {ynBox(name, 'N', 'No')}
        </View>
    );

    const text = (name, props = {}) => (
        <TextInput
            value={String(report[name] ?? '')}
            editable={!readOnly}
            onChangeText={set(name)}
            style={{ ...inputBase, ...roStyle }}
            {...props}
        />
    );

    return (
        <View style={{ paddingBottom: 8 }}>
            {embedded ? null : (
            <View style={{ borderRadius: 12, backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', padding: 14, marginBottom: 14 }}>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 16, rowGap: 10, justifyContent: 'space-between' }}>
                    <View style={{ flex: 1, minWidth: 220 }}>
                        <Text style={{ fontSize: 12, fontWeight: '600', color: '#1D4ED8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Project</Text>
                        <Text style={{ fontSize: 16, fontWeight: '700', color: '#0F172A', marginTop: 2 }}>
                            {`${report.buId}${jobName ? ' — ' + jobName : ''}`}
                        </Text>
                        <Text style={{ fontSize: 13, color: '#334155', marginTop: 4 }}>
                            {report.reportNumber
                                ? `Report #${report.reportNumber} · ${report.status}`
                                : 'Report # will be assigned on first save.'}
                        </Text>
                        {workTypes.length > 0 && (
                            <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 6, rowGap: 4, marginTop: 6 }}>
                                {workTypes.map((t) => (
                                    <View key={t} style={{ borderRadius: 999, border: '1px solid #93C5FD', backgroundColor: '#DBEAFE', paddingTop: 2, paddingBottom: 2, paddingLeft: 8, paddingRight: 8 }}>
                                        <Text style={{ fontSize: 11, fontWeight: '600', color: '#1E40AF' }}>{t}</Text>
                                    </View>
                                ))}
                            </View>
                        )}
                    </View>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 12, rowGap: 10 }}>
                        <View>
                            <Text style={labelStyle}>Report Date</Text>
                            <Text style={{ fontSize: 16, fontWeight: '600', color: '#0F172A', paddingTop: 10, paddingBottom: 10 }}>
                                {fmtDate(report.reportDate)}
                            </Text>
                        </View>
                        <View style={{ width: 200 }}>
                            <Text style={labelStyle}>Prepared By</Text>
                            {text('preparedBy', { placeholder: 'Signed-in user' })}
                        </View>
                    </View>
                </View>
                <WeatherBlock readOnly={readOnly} blue />
            </View>
            )}

            <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: '4%' }}>
                <FlexField label="Ground Conditions">{text('groundConditions', { placeholder: 'Dry / Firm' })}</FlexField>
                <FlexField label="Superintendent">
                    <input
                        type="text"
                        list="fr-supers"
                        value={String(report.superintendent ?? '')}
                        disabled={readOnly}
                        placeholder="Pick from list or type a name"
                        onChange={(e) => set('superintendent')(e.target.value)}
                        style={{ ...inputBase, ...roStyle }}
                    />
                    <datalist id="fr-supers">
                        {supers.map((name) => <option key={name} value={name} />)}
                    </datalist>
                </FlexField>
                {/* Prepared By rode the blue project card; embedded it joins
                    the field grid so it stays editable. */}
                {embedded && (
                    <FlexField label="Prepared By">{text('preparedBy', { placeholder: 'Signed-in user' })}</FlexField>
                )}
            </View>

            {/* Quick Report: the safety question and the issues question share
                one compact row — no section headings, no stacked boxes. Yes on
                safety opens the count + what happened underneath. */}
            {simple ? (
                <>
                    <View style={{
                        border: '1px solid #CBD5E1', borderRadius: 8, marginTop: 2, marginBottom: 10,
                        backgroundColor: readOnly ? '#F1F5F9' : '#fff',
                        flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center',
                    }}>
                        <View style={{
                            flex: 1, minWidth: 250,
                            flexDirection: 'row', alignItems: 'center', minHeight: 38,
                            paddingLeft: 12, paddingRight: 12, columnGap: 12,
                        }}>
                            <Text style={{ flex: 1, fontSize: 14, color: '#0F172A' }}>Any safety incidents today?</Text>
                            {['Y', 'N'].map((v) => (
                                <label
                                    key={v}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 6, width: 54,
                                        cursor: readOnly ? 'default' : 'pointer',
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={anyIncidents === v}
                                        disabled={readOnly}
                                        onChange={(e) => setAnyIncidents(e.target.checked ? v : '')}
                                        style={{ width: 17, height: 17, accentColor: '#1D4ED8', cursor: 'inherit' }}
                                    />
                                    <Text style={{ fontSize: 14, color: '#0F172A' }}>{v === 'Y' ? 'Yes' : 'No'}</Text>
                                </label>
                            ))}
                        </View>
                        <View style={{
                            flex: 1, minWidth: 250,
                            flexDirection: 'row', alignItems: 'center', minHeight: 38,
                            paddingLeft: 12, paddingRight: 12,
                        }}>
                            {readOnly ? (
                                <Text style={{ fontSize: 14, color: '#64748B' }}>
                                    {hasIssues ? 'Issues logged below.' : 'No issues reported.'}
                                </Text>
                            ) : (
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={issuesOpen}
                                        onChange={(e) => toggleIssues(e.target.checked)}
                                        style={{ width: 17, height: 17, accentColor: '#1D4ED8', cursor: 'inherit' }}
                                    />
                                    <Text style={{ fontSize: 14, color: '#0F172A' }}>Any issues or delays today?</Text>
                                </label>
                            )}
                        </View>
                    </View>
                    {anyIncidents === 'Y' && (
                        <>
                            <View style={{ width: 140, marginBottom: 12 }}>
                                <Text style={labelStyle}>How Many</Text>
                                <TextInput
                                    value={String(safety.incidents ?? '')}
                                    editable={!readOnly}
                                    keyboardType="numeric"
                                    inputMode="numeric"
                                    onChangeText={setSafety('incidents')}
                                    style={{ ...inputBase, ...roStyle }}
                                />
                            </View>
                            <WideField label="What Happened">
                                <AutoGrowTextarea
                                    value={String(safety.incidentDetail ?? '')}
                                    disabled={readOnly}
                                    minHeight={90}
                                    placeholder="What happened, who was involved, what was done about it…"
                                    onChange={(e) => setSafety('incidentDetail')(e.target.value)}
                                    style={{ ...inputBase, ...roStyle, fontFamily: 'inherit', display: 'block' }}
                                />
                            </WideField>
                        </>
                    )}
                </>
            ) : (
                <>
                    <Text style={{ fontSize: 13, fontWeight: '700', color: '#0F172A', marginTop: 6, marginBottom: 8 }}>Safety</Text>
                    <View style={{ flexDirection: 'row', columnGap: 12, marginBottom: 12 }}>
                        <View style={{ width: 140 }}>
                            <Text style={labelStyle}>Safety Events</Text>
                            <TextInput
                                value={String(safety.incidents ?? '')}
                                editable={!readOnly}
                                keyboardType="numeric"
                                inputMode="numeric"
                                onChangeText={setSafety('incidents')}
                                style={{ ...inputBase, ...roStyle }}
                            />
                        </View>
                        <View style={{ flex: 1, maxWidth: 280 }}>
                            <Text style={labelStyle}>Safety Rep</Text>
                            <TextInput
                                value={String(safety.safetyRep ?? '')}
                                editable={!readOnly}
                                placeholder="Name"
                                onChangeText={setSafety('safetyRep')}
                                style={{ ...inputBase, ...roStyle }}
                            />
                        </View>
                    </View>
                    <View style={{
                        border: '1px solid #CBD5E1', borderRadius: 8, marginBottom: 12,
                        backgroundColor: readOnly ? '#F1F5F9' : '#fff',
                    }}>
                        {ynRow('safetyMeeting', 'Safety Meeting Held?')}
                        {ynRow('jsaReview', 'JSA reviewed with crews')}
                        {ynRow('sspEapReview', 'Have you reviewed your Site Specific Safety Plan (SSSP)?')}
                        {ynRow('eapKnown', 'Do you know the Emergency Action Plan (EAP)?', true)}
                    </View>
                    <WideField label="Safety Observations / Coaching">
                        <AutoGrowTextarea
                            value={String(safety.safetyObservations ?? '')}
                            disabled={readOnly}
                            minHeight={90}
                            placeholder="Observations from the field, coaching opportunities…"
                            onChange={(e) => setSafety('safetyObservations')(e.target.value)}
                            style={{ ...inputBase, ...roStyle, fontFamily: 'inherit', display: 'block' }}
                        />
                    </WideField>
                </>
            )}

            {/* Full report keeps Issues / Delays as its own section; the Quick
                Report asks the question up in the row above. */}
            {!simple && (
                <>
                    <Text style={{ fontSize: 13, fontWeight: '700', color: '#0F172A', marginTop: 6, marginBottom: 8 }}>Issues / Delays</Text>
                    {readOnly ? (
                        !hasIssues && (
                            <Text style={{ fontSize: 14, color: '#64748B', marginBottom: 12 }}>No issues reported.</Text>
                        )
                    ) : (
                        <label style={{
                            display: 'flex', alignItems: 'center', gap: 8, minHeight: 44,
                            marginBottom: issuesOpen ? 10 : 4, cursor: 'pointer',
                        }}>
                            <input
                                type="checkbox"
                                checked={issuesOpen}
                                onChange={(e) => toggleIssues(e.target.checked)}
                                style={{ width: 18, height: 18, accentColor: '#1D4ED8', cursor: 'inherit' }}
                            />
                            <Text style={{ fontSize: 15, color: '#0F172A' }}>Any issues or delays today?</Text>
                        </label>
                    )}
                </>
            )}
            {issuesOpen && <SectionRowsStep sectionKey="delays" readOnly={readOnly} />}
        </View>
    );
};

export default SetupStep;
